import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Observable, from, map, switchMap } from 'rxjs';
import { Document, Folder } from '../interfaces/document.interface';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {
  constructor(
    private firestore: AngularFirestore,
    private storage: AngularFireStorage,
    private authService: AuthService
  ) {}

  // Folder Methods
  getFolders(parentId?: string | null): Observable<Folder[]> {
    return this.firestore
      .collection<Folder>('folders', ref => {
        let query = ref.orderBy('name');
        if (parentId) {
          query = query.where('parentId', '==', parentId);
        } else {
          query = query.where('parentId', '==', null);
        }
        return query;
      })
      .valueChanges({ idField: 'id' });
  }

  async createFolder(name: string, description?: string, parentId?: string | null): Promise<string> {
    const user = await this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    const folder: Omit<Folder, 'id'> = {
      name,
      description,
      parentId: parentId ?? null,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user.uid
    };

    const docRef = await this.firestore.collection('folders').add(folder);
    return docRef.id;
  }

  async updateFolder(folderId: string, updates: Partial<Folder>): Promise<void> {
    const updateData = {
      ...updates,
      updatedAt: new Date()
    };
    await this.firestore.doc(`folders/${folderId}`).update(updateData);
  }

  async deleteFolder(folderId: string): Promise<void> {
    // First, recursively delete all subfolders and their contents
    const subfolders = await this.firestore
      .collection<Folder>('folders', ref => ref.where('parentId', '==', folderId))
      .get()
      .toPromise();

    const documents = await this.firestore
      .collection<Document>('documents', ref => ref.where('folderId', '==', folderId))
      .get()
      .toPromise();

    const batch = this.firestore.firestore.batch();

    // Delete subfolders recursively
    if (subfolders) {
      for (const subfolder of subfolders.docs) {
        await this.deleteFolder(subfolder.id);
      }
    }

    // Delete documents in the folder
    if (documents) {
      for (const doc of documents.docs) {
        const document = doc.data();
        // Delete file from storage
        await this.storage.refFromURL(document.fileUrl).delete().toPromise();
        // Add document deletion to batch
        batch.delete(this.firestore.doc(`documents/${doc.id}`).ref);
      }
    }

    // Delete the folder itself
    batch.delete(this.firestore.doc(`folders/${folderId}`).ref);

    // Commit the batch
    await batch.commit();
  }

  // Document Methods
  getDocuments(folderId?: string | null): Observable<Document[]> {
    return this.firestore
      .collection<Document>('documents', ref => {
        let query = ref.orderBy('name');
        if (folderId) {
          query = query.where('folderId', '==', folderId);
        } else {
          query = query.where('folderId', '==', null);
        }
        return query;
      })
      .valueChanges({ idField: 'id' });
  }

  async uploadDocument(
    file: File,
    name: string,
    description?: string,
    folderId?: string | null
  ): Promise<string> {
    const user = await this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    // Create a unique file path
    const filePath = `documents/${new Date().getTime()}_${file.name}`;
    const fileRef = this.storage.ref(filePath);
    
    // Upload file
    const task = this.storage.upload(filePath, file);
    
    // Get the download URL after upload completes
    const downloadUrl = await task.then(() => fileRef.getDownloadURL().toPromise());

    // Create document record
    const document: Omit<Document, 'id'> = {
      name,
      description,
      fileUrl: downloadUrl,
      fileType: file.type,
      fileSize: file.size,
      folderId: folderId ?? null,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user.uid,
      uploadedBy: user.uid
    };

    const docRef = await this.firestore.collection('documents').add(document);
    return docRef.id;
  }

  async updateDocument(documentId: string, updates: Partial<Document>): Promise<void> {
    const updateData = {
      ...updates,
      updatedAt: new Date()
    };
    await this.firestore.doc(`documents/${documentId}`).update(updateData);
  }

  async deleteDocument(documentId: string): Promise<void> {
    // Get the document data first
    const doc = await this.firestore.doc(`documents/${documentId}`).get().toPromise();
    if (!doc?.exists) return;

    const document = doc.data() as Document;

    // Delete the file from storage
    await this.storage.refFromURL(document.fileUrl).delete().toPromise();

    // Delete the document record
    await this.firestore.doc(`documents/${documentId}`).delete();
  }

  // Helper Methods
  getBreadcrumb(folderId?: string): Observable<Folder[]> {
    if (!folderId) return from([[]]);

    return this.firestore.doc<Folder>(`folders/${folderId}`).get().pipe(
      switchMap(async folderDoc => {
        const breadcrumb: Folder[] = [];
        let currentFolder = folderDoc.data() as Folder;
        currentFolder.id = folderDoc.id;
        
        while (currentFolder) {
          breadcrumb.unshift(currentFolder);
          if (!currentFolder.parentId) break;
          
          const parentDoc = await this.firestore.doc<Folder>(`folders/${currentFolder.parentId}`).get().toPromise();
          if (!parentDoc?.exists) break;
          
          currentFolder = parentDoc.data() as Folder;
          currentFolder.id = parentDoc.id;
        }
        
        return breadcrumb;
      })
    );
  }
} 